import { PlainTime } from 'temporal-luxon';
/**
 * Function to check if all options are selected.
 * @param internalOptions - The internal selected options of the time picker.
 * @param showDayPeriod - Boolean to indicate if the day period option should be considered.
 * @param showTimeZone - Boolean to indicate if the timezone option should be considered.
 * @returns true if all relevant options are selected, false otherwise.
 */
export const areAllOptionsSelected = (internalOptions, showDayPeriod, showTimeZone) => {
    let everyOptionHasValue = internalOptions.hour.option !== null && internalOptions.minute.option !== null;
    if (showDayPeriod) {
        everyOptionHasValue = everyOptionHasValue && internalOptions.dayPeriod.option !== null;
    }
    if (showTimeZone) {
        everyOptionHasValue = everyOptionHasValue && internalOptions.timeZoneName.option !== null;
    }
    return everyOptionHasValue;
};
/*
 * Function to check if all options are filled.
 * @param options - The internal selected options of the time picker.
 * @param showTimeZone - Boolean to indicate if the timezone option should be considered.
 * @param shouldBeFilled - Boolean to indicate if the options should be filled or not.
 * @returns true if all relevant options are filled, false otherwise.
 */
export const areOptionsFilled = (options, showTimeZone, shouldBeFilled = true) => {
    return Object.values(options).some((option) => {
        const hasOption = Boolean(option.option);
        return shouldBeFilled === hasOption && (showTimeZone || option !== options.timeZoneName);
    });
};
/**
 * Array of combobox options for 12-hour time format.
 * Each option contains a label and a value.
 * The label is a zero-padded string representation of the hour, while the value is the actual hour (with 12 represented as 0).
 */
export const getHours12Options = ({ timeShortFormatter }) => Array.from(Array(12).keys())
    .map((value) => (value + 1) % 12)
    .map((value) => {
    var _a, _b;
    const formattedValue = (_b = (_a = timeShortFormatter.formatToParts(new PlainTime(value, 0)).find(({ type }) => type === 'hour')) === null || _a === void 0 ? void 0 : _a.value) !== null && _b !== void 0 ? _b : '';
    return {
        label: formattedValue,
        value,
    };
});
/**
 * Array of combobox options for 24-hour time format.
 * Each option contains a label and a value.
 * The label is a zero-padded string representation of the hour, while the value is the actual hour.
 */
export const getHours24Options = ({ timeShortFormatter }) => Array.from(Array(24).keys()).map((value) => {
    var _a, _b;
    const formattedValue = (_b = (_a = timeShortFormatter.formatToParts(new PlainTime(value, 0)).find(({ type }) => type === 'hour')) === null || _a === void 0 ? void 0 : _a.value) !== null && _b !== void 0 ? _b : '';
    return {
        label: formattedValue,
        value,
    };
});
/**
 * Array of combobox options for minutes.
 * Each option contains a label and a value.
 * The label is a zero-padded string representation of the minute, while the value is the actual minute.
 */
export const getMinuteOptions = ({ timeShortFormatter, minutesOptions, }) => {
    const minutsArray = minutesOptions !== null && minutesOptions !== void 0 ? minutesOptions : Array.from(Array(60).keys());
    return minutsArray.map((value) => {
        var _a, _b;
        const formattedValue = (_b = (_a = timeShortFormatter.formatToParts(new PlainTime(0, value)).find(({ type }) => type === 'minute')) === null || _a === void 0 ? void 0 : _a.value) !== null && _b !== void 0 ? _b : value.toString();
        return {
            label: formattedValue,
            value,
        };
    });
};
/**
 * Array of combobox options for timezones.
 * Each option contains a label and a value, both representing a timezone.
 */
export const timezoneOptions = [
    { label: 'UTC-12:00', value: 'UTC-12:00' },
    { label: 'UTC-11:00', value: 'UTC-11:00' },
    { label: 'UTC-10:00', value: 'UTC-10:00' },
    { label: 'UTC-09:00', value: 'UTC-09:00' },
    { label: 'UTC-08:00', value: 'UTC-08:00' },
    { label: 'UTC-07:00', value: 'UTC-07:00' },
    { label: 'UTC-06:00', value: 'UTC-06:00' },
    { label: 'UTC-05:00', value: 'UTC-05:00' },
    { label: 'UTC-04:00', value: 'UTC-04:00' },
    { label: 'UTC-03:00', value: 'UTC-03:00' },
    { label: 'UTC-02:00', value: 'UTC-02:00' },
    { label: 'UTC-01:00', value: 'UTC-01:00' },
    { label: 'UTC+00:00', value: 'UTC+00:00' },
    { label: 'UTC+01:00', value: 'UTC+01:00' },
    { label: 'UTC+02:00', value: 'UTC+02:00' },
    { label: 'UTC+03:00', value: 'UTC+03:00' },
    { label: 'UTC+04:00', value: 'UTC+04:00' },
    { label: 'UTC+05:00', value: 'UTC+05:00' },
    { label: 'UTC+06:00', value: 'UTC+06:00' },
    { label: 'UTC+07:00', value: 'UTC+07:00' },
    { label: 'UTC+08:00', value: 'UTC+08:00' },
    { label: 'UTC+09:00', value: 'UTC+09:00' },
    { label: 'UTC+10:00', value: 'UTC+10:00' },
    { label: 'UTC+11:00', value: 'UTC+11:00' },
    { label: 'UTC+12:00', value: 'UTC+12:00' },
    { label: 'UTC+13:00', value: 'UTC+13:00' },
    { label: 'UTC+14:00', value: 'UTC+14:00' },
];
/**
 * Array of combobox options for AM/PM selection.
 * Each option contains a label and a value, both representing either AM or PM.
 */
export const getDayPeriodOptions = ({ timeShortFormatter }) => {
    var _a, _b, _c, _d;
    const amValue = (_b = (_a = timeShortFormatter.formatToParts(new PlainTime(0, 0)).find(({ type }) => type === 'dayPeriod')) === null || _a === void 0 ? void 0 : _a.value) !== null && _b !== void 0 ? _b : '';
    const pmValue = (_d = (_c = timeShortFormatter.formatToParts(new PlainTime(12, 0)).find(({ type }) => type === 'dayPeriod')) === null || _c === void 0 ? void 0 : _c.value) !== null && _d !== void 0 ? _d : '';
    return [
        { label: amValue, value: amValue },
        { label: pmValue, value: pmValue },
    ];
};
//# sourceMappingURL=time_picker_utils.js.map